import { useTranslation } from "react-i18next";
import {
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ChartBarIcon,
} from "@heroicons/react/24/outline";
import { PMDialog } from "../../utils/PMDialog";
import { PeriodSelector } from "../hours/period_selector";
import { LaborSettlementDetails } from "./LaborSettlementDetails";
import { CalculationInfoIcon } from "./CalculationInfoIcon";
import { SUMMARY_CALC_GUIDE_KEYS } from "./AccountingSummaryCalcGuides";
import { signedFinancialColorClass } from "../../../utils/signedFinancialColor";

const Row = ({
  label,
  value,
  valueClass = "text-slate-900",
  hint,
  strong,
  onInfoClick,
  infoAriaLabel,
}) => (
  <div className="flex items-start justify-between gap-4 text-sm">
    <div className="min-w-0">
      <div className="flex items-center gap-1.5">
        <span className={strong ? "font-semibold text-slate-900" : "text-slate-600"}>
          {label}
        </span>
        {onInfoClick && (
          <CalculationInfoIcon
            ariaLabel={infoAriaLabel}
            onClick={onInfoClick}
          />
        )}
      </div>
      {hint && <p className="mt-0.5 text-xs text-slate-500">{hint}</p>}
    </div>
    <span className={`shrink-0 font-semibold tabular-nums ${valueClass}`}>
      {value}
    </span>
  </div>
);

export const AccountingSummaryDetailDialog = ({
  open,
  onClose,
  summary,
  formatCurrency,
  selectedYear,
  period,
  onPeriodChange,
  onNavigate,
  onOpenCalcGuide,
}) => {
  const { t } = useTranslation();

  if (!summary) return null;

  const financialColor = (value, neutralClass = "text-slate-900") =>
    signedFinancialColorClass(value, { neutralClass });

  const profit = parseFloat(summary.profit || 0);
  const profitMargin =
    typeof summary.profit_margin === "string"
      ? parseFloat(summary.profit_margin)
      : summary.profit_margin || 0;

  const totalCosts = parseFloat(summary.costs?.total || 0);
  const customerInvoiceIncome = parseFloat(summary.income?.customer_invoices || 0);
  const totalIncome = parseFloat(summary.income?.total || customerInvoiceIncome);

  const laborData = summary.costs?.labor;
  const laborCosts = parseFloat(laborData?.total ?? laborData ?? 0);
  const supplierInvoiceCosts = parseFloat(summary.costs?.supplier_invoices || 0);
  const itemListCosts = parseFloat(summary.costs?.item_lists || 0);
  const voucherExpenseCosts = parseFloat(summary.costs?.voucher_expenses || 0);

  const expectedReportIncome = parseFloat(summary.expected_report_income?.income || 0);
  const expectedReportCosts = parseFloat(summary.expected_report_income?.costs || 0);
  const expectedReportProfit = parseFloat(summary.expected_report_income?.profit || 0);
  const hasExpected = expectedReportIncome > 0 || expectedReportCosts > 0;

  const guide = (key) =>
    onOpenCalcGuide
      ? {
          onInfoClick: () => onOpenCalcGuide(key),
          infoAriaLabel: t(SUMMARY_CALC_GUIDE_KEYS[key].ariaKey),
        }
      : {};

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title="accounting.summary.title"
      size="xl"
      closeOnOutsideClick
    >
      <div className="space-y-5">
        {/* Period selector */}
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          <PeriodSelector
            periodType={period.periodType}
            from={period.from}
            to={period.to}
            allowedPeriodTypes={["year"]}
            onChange={onPeriodChange}
          />
        </div>

        {/* Profit */}
        <div className="rounded-2xl border border-slate-200 bg-slate-950 p-4 text-white">
          <div className="mb-1 flex items-center gap-2">
            {profit >= 0 ? (
              <ArrowTrendingUpIcon className="h-5 w-5 text-emerald-300" />
            ) : (
              <ArrowTrendingDownIcon className="h-5 w-5 text-red-300" />
            )}
            <span className="text-xs font-semibold uppercase tracking-wide text-slate-300">
              {t("accounting.summary.profit")}
            </span>
            {onOpenCalcGuide && (
              <CalculationInfoIcon
                className="ml-auto text-slate-400 hover:bg-slate-800 hover:text-slate-200"
                ariaLabel={t(SUMMARY_CALC_GUIDE_KEYS.profit.ariaKey)}
                onClick={() => onOpenCalcGuide("profit")}
              />
            )}
          </div>
          <div
            className={`mb-1 text-2xl font-bold ${
              profit > 0
                ? "text-emerald-300"
                : profit < 0
                  ? "text-red-300"
                  : "text-white"
            }`}
          >
            {formatCurrency(profit)}
          </div>
          <div
            className={`text-xs ${
              profitMargin > 0
                ? "text-emerald-300"
                : profitMargin < 0
                  ? "text-red-300"
                  : "text-slate-300"
            }`}
          >
            {profitMargin >= 0 ? "+" : ""}
            {profitMargin.toFixed(1)}% {t("accounting.summary.profit_margin")}
          </div>
          <p className="mt-2 text-xs text-slate-400">
            {t("accounting.summary.profit_explanation")}
          </p>
        </div>

        {/* Income */}
        <div className="space-y-2 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <Row
            label={t("accounting.summary.income")}
            value={formatCurrency(totalIncome)}
            valueClass={financialColor(totalIncome)}
            strong
            {...guide("income")}
          />
          <Row
            label={t("accounting.summary.customer_invoices")}
            value={formatCurrency(customerInvoiceIncome)}
            valueClass={financialColor(customerInvoiceIncome)}
          />
          <p className="pt-1 text-xs text-slate-500">
            {t("accounting.summary.income_explanation")}
          </p>
        </div>

        {/* Costs with breakdown */}
        <div className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
          <Row
            label={t("accounting.summary.costs")}
            value={formatCurrency(totalCosts)}
            valueClass={financialColor(totalCosts)}
            strong
            {...guide("costs")}
          />
          <Row
            label={t("accounting.summary.labor")}
            value={formatCurrency(laborCosts)}
            valueClass="text-slate-900"
            {...guide("labor")}
          />
          {laborData && (
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
              <LaborSettlementDetails
                labor={laborData}
                t={t}
                formatCurrency={formatCurrency}
                translationPrefix="accounting.summary"
              />
            </div>
          )}
          <Row
            label={t("accounting.summary.supplier_invoices")}
            value={formatCurrency(supplierInvoiceCosts)}
            valueClass="text-slate-900"
            {...guide("supplier_invoices")}
          />
          <Row
            label={t("accounting.summary.voucher_expenses")}
            value={formatCurrency(voucherExpenseCosts)}
            valueClass="text-slate-900"
            {...guide("voucher_expenses")}
          />
          <Row
            label={t("accounting.summary.item_lists")}
            value={formatCurrency(itemListCosts)}
            valueClass="text-slate-500"
            {...guide("item_lists")}
          />
          <p className="pt-1 text-xs text-slate-500">
            {t("accounting.summary.item_lists_excluded_hint")}
          </p>
          <p className="pt-1 text-xs text-slate-500">
            {t("accounting.summary.labor_voucher_explanation")}
          </p>
        </div>

        {/* Expected report income (div+role to avoid nested buttons with info icon) */}
        {hasExpected && (
          <div
            role="button"
            tabIndex={0}
            onClick={() => {
              onClose();
              onNavigate(
                `/dashboard/accounting/projects-expected-income?year=${selectedYear}`
              );
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClose();
                onNavigate(
                  `/dashboard/accounting/projects-expected-income?year=${selectedYear}`
                );
              }
            }}
            className="w-full cursor-pointer space-y-2 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-left transition hover:border-slate-300 hover:bg-slate-100/80 focus:outline-none focus:ring-2 focus:ring-slate-300"
          >
            <div className="flex items-center gap-2">
              <ChartBarIcon className="h-5 w-5 text-slate-700" />
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                {t("accounting.summary.expected_report_income")}
              </span>
              {onOpenCalcGuide && (
                <CalculationInfoIcon
                  ariaLabel={t(SUMMARY_CALC_GUIDE_KEYS.expected_report_income.ariaKey)}
                  onClick={() => onOpenCalcGuide("expected_report_income")}
                />
              )}
              <span className="ml-auto text-xs text-slate-500">
                {t("accounting.summary.view_projects")}
              </span>
            </div>
            <p className="text-xs text-slate-500">
              {t("accounting.summary.expected_report_income_hint")}
            </p>
            <Row
              label={t("accounting.summary.expected_income")}
              value={formatCurrency(expectedReportIncome)}
              valueClass={financialColor(expectedReportIncome)}
            />
            <Row
              label={t("accounting.summary.expected_costs")}
              value={formatCurrency(expectedReportCosts)}
              valueClass={financialColor(expectedReportCosts)}
            />
            <div className="border-t border-slate-200 pt-2">
              <Row
                label={t("accounting.summary.expected_profit")}
                value={formatCurrency(expectedReportProfit)}
                valueClass={financialColor(expectedReportProfit)}
                strong
              />
            </div>
          </div>
        )}
      </div>
    </PMDialog>
  );
};
